import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Download, RefreshCw, FileText, Check, Mail, Settings2, Image as ImageIcon, X } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, AlignmentType, ImageRun } from 'docx';
import { liteApi } from '@/utils/lite/liteApi';
import LiteFeedback from '@/components/lite/LiteFeedback';

const HEADER_KEY = 'playfirst_lite_header';

function loadHeader() {
  try {
    const raw = localStorage.getItem(HEADER_KEY);
    if (!raw) return { clinician_name: '', clinic_name: '', date: '', title: '', logo: '' };
    return { clinician_name: '', clinic_name: '', date: '', title: '', logo: '', ...JSON.parse(raw) };
  } catch {
    return { clinician_name: '', clinic_name: '', date: '', title: '', logo: '' };
  }
}

function saveHeader(h) {
  try { localStorage.setItem(HEADER_KEY, JSON.stringify(h)); } catch {}
}

/**
 * OutputView v2 — final shared step. Renders output with custom header
 * support and 4 export options: Copy, PDF, Word (.docx), Email me a copy.
 *
 * Header info is stored in localStorage only — never sent to the AI prompt
 * and never persisted server-side.
 */
export default function OutputView({
  output,
  toolLabel = 'Output',
  tool = '',  // e.g. 'report' | 'parent' | 'glp' | 'activity' — used for feedback widget
  onRegenerate,
  onStartOver,
  usage,
  onWaitlist,
}) {
  const [copied, setCopied] = useState(false);
  const [headerOpen, setHeaderOpen] = useState(false);
  const [header, setHeader] = useState(loadHeader());
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailValue, setEmailValue] = useState(() => {
    try { return localStorage.getItem('playfirst_lite_email') || ''; } catch { return ''; }
  });
  const [emailError, setEmailError] = useState('');

  useEffect(() => { saveHeader(header); }, [header]);
  const updateHeader = (k, v) => setHeader((h) => ({ ...h, [k]: v }));

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024) { alert('Please use a logo under 200KB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => updateHeader('logo', ev.target.result);
    reader.readAsDataURL(file);
  };

  // --- Copy ---
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(output || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  // --- PDF ---
  const handleDownloadPdf = () => {
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'letter' });
      const margin = 54;
      const pageWidth = doc.internal.pageSize.getWidth() - margin * 2;
      const pageHeight = doc.internal.pageSize.getHeight();
      let cursorY = margin;

      // Header (logo + clinic + clinician + date)
      const hasHeader = header.clinic_name || header.clinician_name || header.logo;
      if (hasHeader) {
        if (header.logo) {
          try {
            doc.addImage(header.logo, 'PNG', margin, cursorY, 60, 60);
            cursorY += 4;
          } catch {/* ignore image errors */}
        }
        const textX = header.logo ? margin + 72 : margin;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text(header.clinic_name || '', textX, cursorY + 14);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(80);
        doc.text(header.clinician_name || '', textX, cursorY + 30);
        doc.setFontSize(10);
        doc.setTextColor(140);
        doc.text(header.date || new Date().toLocaleDateString(), textX, cursorY + 46);
        cursorY += 76;
        doc.setDrawColor(220);
        doc.line(margin, cursorY, margin + pageWidth, cursorY);
        cursorY += 18;
        doc.setTextColor(0);
      }

      // Title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.text(header.title || `${toolLabel} (Draft)`, margin, cursorY);
      cursorY += 22;

      // Body
      doc.setFontSize(11);
      const lines = (output || '').split('\n');
      lines.forEach((rawLine) => {
        const isH2 = /^##\s/.test(rawLine);
        const isH3 = /^###\s/.test(rawLine);
        const isH = isH2 || isH3;
        const text = rawLine.replace(/^#+\s*/, '').replace(/\*\*/g, '');
        doc.setFont('helvetica', isH ? 'bold' : 'normal');
        doc.setFontSize(isH2 ? 12 : isH3 ? 11 : 11);
        const wrapped = doc.splitTextToSize(text || ' ', pageWidth);
        wrapped.forEach((w) => {
          if (cursorY > pageHeight - margin) {
            doc.addPage();
            cursorY = margin;
          }
          doc.text(w, margin, cursorY);
          cursorY += isH2 ? 16 : 14;
        });
        if (isH) cursorY += 2;
      });

      // Footer disclaimer on last page
      if (cursorY > pageHeight - margin - 30) doc.addPage();
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(140);
      doc.text('AI-generated draft. Clinician review required. Generated by PlayFirst Lite.',
        margin, pageHeight - margin / 2);

      doc.save(`playfirst-lite-${toolLabel.toLowerCase().replace(/\s+/g, '-')}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed. Try Copy instead.');
    }
  };

  // --- Word (.docx) ---
  const handleDownloadDocx = async () => {
    try {
      const children = [];

      // Header block
      if (header.logo) {
        try {
          // Convert dataURL -> arraybuffer
          const res = await fetch(header.logo);
          const ab = await res.arrayBuffer();
          children.push(new Paragraph({
            children: [new ImageRun({ data: ab, transformation: { width: 60, height: 60 } })],
          }));
        } catch {/* ignore */}
      }
      if (header.clinic_name) {
        children.push(new Paragraph({
          children: [new TextRun({ text: header.clinic_name, bold: true, size: 26 })],
          spacing: { after: 60 },
        }));
      }
      if (header.clinician_name) {
        children.push(new Paragraph({
          children: [new TextRun({ text: header.clinician_name, size: 22, color: '57534e' })],
          spacing: { after: 40 },
        }));
      }
      children.push(new Paragraph({
        children: [new TextRun({ text: header.date || new Date().toLocaleDateString(), size: 20, color: '8a8784' })],
        spacing: { after: 240 },
      }));

      // Title
      children.push(new Paragraph({
        text: header.title || `${toolLabel} (Draft)`,
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 200 },
      }));

      // Body
      (output || '').split('\n').forEach((line) => {
        if (/^##\s/.test(line)) {
          children.push(new Paragraph({
            text: line.replace(/^##\s*/, ''),
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 80 },
          }));
        } else if (/^###\s/.test(line)) {
          children.push(new Paragraph({
            text: line.replace(/^###\s*/, ''),
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 160, after: 60 },
          }));
        } else if (/^[\s]*[-•*]\s/.test(line)) {
          children.push(new Paragraph({
            text: line.replace(/^[\s]*[-•*]\s*/, '').replace(/\*\*/g, ''),
            bullet: { level: 0 },
            spacing: { after: 40 },
          }));
        } else if (line.trim() === '') {
          children.push(new Paragraph({ text: '' }));
        } else {
          // Convert **bold** inline
          const runs = [];
          const parts = line.split(/(\*\*[^*]+\*\*)/g);
          parts.forEach((p) => {
            if (/^\*\*[^*]+\*\*$/.test(p)) {
              runs.push(new TextRun({ text: p.replace(/\*\*/g, ''), bold: true }));
            } else if (p) {
              runs.push(new TextRun({ text: p }));
            }
          });
          children.push(new Paragraph({ children: runs, spacing: { after: 80 } }));
        }
      });

      children.push(new Paragraph({
        children: [new TextRun({
          text: 'AI-generated draft. Clinician review required. Generated by PlayFirst Lite.',
          italics: true, size: 18, color: '8a8784',
        })],
        alignment: AlignmentType.LEFT,
        spacing: { before: 320 },
      }));

      const doc = new Document({ sections: [{ children }] });
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `playfirst-lite-${toolLabel.toLowerCase().replace(/\s+/g, '-')}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Word export failed:', err);
      alert('Word export failed. Try PDF or Copy instead.');
    }
  };

  // --- Email ---
  const handleEmailMe = async () => {
    setEmailError('');
    if (!emailValue || !emailValue.includes('@')) {
      setEmailError('Please enter a valid email.');
      return;
    }
    setEmailing(true);
    try {
      await liteApi.emailReport({
        email: emailValue,
        tool: toolLabel,
        output,
        header: {
          clinician_name: header.clinician_name,
          clinic_name: header.clinic_name,
          date: header.date,
          title: header.title || `${toolLabel} (Draft)`,
        },
      });
      try { localStorage.setItem('playfirst_lite_email', emailValue); } catch {}
      setEmailSent(true);
      setTimeout(() => { setEmailModalOpen(false); setEmailSent(false); }, 1800);
    } catch (e) {
      setEmailError(e.message || 'Could not send email.');
    } finally {
      setEmailing(false);
    }
  };

  const renderMarkdown = (text) => {
    return (text || '').split('\n').map((line, i) => {
      if (/^###\s/.test(line)) return <h3 key={i} className="text-base font-semibold text-primary-900 mt-4 mb-1">{line.replace(/^###\s*/, '')}</h3>;
      if (/^##\s/.test(line)) return <h2 key={i} className="text-lg font-semibold text-primary-900 mt-5 mb-2">{line.replace(/^##\s*/, '')}</h2>;
      if (/^#\s/.test(line)) return <h1 key={i} className="text-xl font-serif text-primary-900 mt-6 mb-2">{line.replace(/^#\s*/, '')}</h1>;
      if (/^[\s]*[-•*]\s/.test(line)) return <li key={i} className="ml-5 list-disc text-stone-700">{renderInline(line.replace(/^[\s]*[-•*]\s*/, ''))}</li>;
      if (line.trim() === '') return <div key={i} className="h-3" />;
      return <p key={i} className="text-stone-700 leading-relaxed">{renderInline(line)}</p>;
    });
  };
  const renderInline = (text) => text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    /^\*\*[^*]+\*\*$/.test(p)
      ? <strong key={i} className="font-semibold text-stone-900">{p.replace(/\*\*/g, '')}</strong>
      : <span key={i}>{p}</span>
  );

  return (
    <div className="space-y-4" data-testid="lite-output-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-stone-500">
          <FileText className="w-4 h-4" />
          <span>{toolLabel} draft · clinician review required</span>
        </div>
        {usage && (
          <span className="text-xs text-stone-500" data-testid="lite-usage-counter">
            {Math.max(0, usage.daily_limit - usage.used_today)} of {usage.daily_limit} generations left today
          </span>
        )}
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-6 max-h-[60vh] overflow-y-auto" data-testid="lite-output-content">
        {(header.clinic_name || header.clinician_name) && (
          <div className="border-b border-stone-100 pb-3 mb-4 flex items-start gap-3">
            {header.logo && <img src={header.logo} alt="" className="w-12 h-12 object-contain rounded" />}
            <div>
              {header.clinic_name && <div className="font-semibold text-stone-900">{header.clinic_name}</div>}
              {header.clinician_name && <div className="text-sm text-stone-600">{header.clinician_name}</div>}
              {header.date && <div className="text-xs text-stone-400">{header.date}</div>}
            </div>
          </div>
        )}
        {renderMarkdown(output)}
      </div>

      {/* Header customization (collapsible) */}
      <div className="rounded-2xl border border-stone-200 bg-white">
        <button
          type="button"
          onClick={() => setHeaderOpen(!headerOpen)}
          className="w-full flex items-center justify-between gap-3 p-4 text-left"
          data-testid="header-toggle-btn"
        >
          <div className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-stone-500" />
            <span className="text-sm font-medium text-stone-900">Add header for export</span>
            <span className="text-xs text-stone-500">{(header.clinic_name || header.clinician_name) ? '· active' : '· optional'}</span>
          </div>
          <span className="text-stone-400 text-sm">{headerOpen ? '▴' : '▾'}</span>
        </button>
        {headerOpen && (
          <div className="px-4 pb-4 space-y-3 border-t border-stone-100">
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div className="space-y-1">
                <Label className="text-xs">Clinician name</Label>
                <Input value={header.clinician_name} onChange={(e) => updateHeader('clinician_name', e.target.value)} placeholder="e.g. Dr. Jane Smith, SLP" data-testid="header-clinician-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Clinic name</Label>
                <Input value={header.clinic_name} onChange={(e) => updateHeader('clinic_name', e.target.value)} placeholder="e.g. PlayFirst Speech & Language" data-testid="header-clinic-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <Input type="date" value={header.date} onChange={(e) => updateHeader('date', e.target.value)} data-testid="header-date-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Report title</Label>
                <Input value={header.title} onChange={(e) => updateHeader('title', e.target.value)} placeholder={`${toolLabel} (Draft)`} data-testid="header-title-input" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs">Logo (optional, &lt;200KB)</Label>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-stone-200 hover:bg-stone-50 cursor-pointer text-sm" data-testid="header-logo-label">
                    <ImageIcon className="w-4 h-4 text-stone-500" />
                    <span>Upload logo</span>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="header-logo-input" />
                  </label>
                  {header.logo && (
                    <>
                      <img src={header.logo} alt="" className="w-10 h-10 object-contain rounded" />
                      <button type="button" onClick={() => updateHeader('logo', '')} className="text-xs text-stone-500 hover:text-red-600" data-testid="header-logo-remove">
                        <X className="w-3 h-3 inline mr-1" /> Remove
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <p className="text-xs text-stone-500 pt-2 border-t border-stone-100">
              🛡️ Header info stays in your browser only. Never sent to AI or stored on our servers.
            </p>
          </div>
        )}
      </div>

      {/* Export buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={handleCopy} variant="outline" className="rounded-full" data-testid="lite-copy-btn">
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button onClick={handleDownloadPdf} variant="outline" className="rounded-full" data-testid="lite-download-pdf-btn">
          <Download className="w-4 h-4 mr-2" /> PDF
        </Button>
        <Button onClick={handleDownloadDocx} variant="outline" className="rounded-full" data-testid="lite-download-docx-btn">
          <Download className="w-4 h-4 mr-2" /> Word (.docx)
        </Button>
        <Button onClick={() => { setEmailModalOpen(true); setEmailSent(false); setEmailError(''); }} variant="outline" className="rounded-full" data-testid="lite-email-btn">
          <Mail className="w-4 h-4 mr-2" /> Email me
        </Button>
        {onRegenerate && (
          <Button onClick={onRegenerate} variant="ghost" className="rounded-full" data-testid="lite-regenerate-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Regenerate
          </Button>
        )}
        {onStartOver && (
          <Button onClick={onStartOver} variant="ghost" className="rounded-full" data-testid="lite-startover-btn">
            New input
          </Button>
        )}
      </div>

      {/* Anonymous thumbs up/down feedback — stateless, no content sent */}
      {tool && output && (
        <LiteFeedback tool={tool} outputKey={(output || '').slice(0, 80)} />
      )}

      {emailModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="email-modal" onClick={() => !emailing && setEmailModalOpen(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-serif text-primary-900 mb-2">Email me a copy</h3>
            <p className="text-sm text-stone-600 mb-4">
              We'll send the full output to your inbox. The email is stored only as a hashed identifier for usage tracking.
            </p>
            {emailSent ? (
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm flex items-center gap-2" data-testid="email-sent-confirmation">
                <Check className="w-4 h-4" /> Sent! Check your inbox.
              </div>
            ) : (
              <>
                <Input type="email" placeholder="you@clinic.com" value={emailValue} onChange={(e) => setEmailValue(e.target.value)} className="mb-2" data-testid="email-input" />
                {emailError && <p className="text-sm text-red-600 mb-2">{emailError}</p>}
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="outline" onClick={() => setEmailModalOpen(false)} className="rounded-full" disabled={emailing} data-testid="email-cancel-btn">Cancel</Button>
                  <Button onClick={handleEmailMe} className="rounded-full" disabled={emailing} data-testid="email-send-btn">
                    {emailing ? 'Sending…' : 'Send'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-stone-100 bg-stone-50 p-4 text-sm text-stone-600">
        <p className="font-medium text-stone-800 mb-1">Save this work to a child profile?</p>
        <p>
          Full PlayFirst (with profile-linked tracking & progress charts) is available outside the US.
          A US tier with full BAA coverage is coming.{' '}
          {onWaitlist && (
            <button onClick={onWaitlist} data-testid="lite-waitlist-cta-inline" className="text-primary underline hover:no-underline font-medium">
              Get notified
            </button>
          )}
        </p>
      </div>
    </div>
  );
}
